// HMAC-SHA256 sign/verify over WebCrypto — the primitive under every
// HS256 token in this repo.
//
// Extracted 2026-07-26 (ladder step 2). C2's guest/room tokens
// (`packages/chat/src/tokens.ts`) and C3's kiosk device token
// (`packages/staff/src/kioskToken.ts`) had each grown their own copy of
// `toBase64Url` / `fromBase64Url` / `hmacKey`; the C3 copy even carried the
// comment "packages/chat tokens.ts pattern". Two contexts maintaining
// byte-identical crypto is how they drift.
//
// WHAT IS DELIBERATELY *NOT* SHARED: the tokens themselves. C2's guest JWT and
// C3's kiosk JWT have intentionally different claim shapes and security
// postures — the kiosk token carries no `role`/`aud` precisely so it can never
// pass as a Supabase token at PostgREST/Realtime. Sharing the primitive is
// hygiene; sharing the minting would blur two separate security decisions.
//
// `CryptoKey` is kept OUT of every public signature. `apps/staff-bridge`
// typechecks `packages/staff` under Node-only types, so a leaked DOM type here
// would break a third package's build.

import { fromBase64Url, toBase64Url } from './base64url.js';

const encoder = new TextEncoder();

async function hmacKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify'],
  );
}

/** base64url HMAC-SHA256 signature over `payload`. */
export async function hmacSign(secret: string, payload: string): Promise<string> {
  const key = await hmacKey(secret);
  const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(payload));
  return toBase64Url(new Uint8Array(sig));
}

/**
 * Constant-time-ish verification via WebCrypto's own `verify` (not a string
 * compare of signatures). Returns false rather than throwing on a malformed
 * signature, so callers can treat "bad token" as one branch.
 */
export async function hmacVerify(
  secret: string,
  payload: string,
  signature: string,
): Promise<boolean> {
  let sigBytes: Uint8Array;
  try {
    sigBytes = fromBase64Url(signature);
  } catch {
    return false;
  }
  const key = await hmacKey(secret);
  return crypto.subtle.verify('HMAC', key, sigBytes as BufferSource, encoder.encode(payload));
}
